import type { IPublicFileAttachment } from '../../../../models/Api';
import type { SectionedSurveyForm } from '../types';
import type { SurveyForm, SurveyPart } from '../../../../../extViewmodels';
import { computeVisibleFields } from './useConditionalVisibility';

/** Value type accepted/returned for each field key. */
type FieldValue = string | IPublicFileAttachment[];

/** Options bag for clearHiddenFieldValues. */
export interface ClearHiddenFieldValuesOptions {
	/**
	 * Per-field cleared value factory. Return [] for FileUpload fields; '' for
	 * everything else. Defaults to returning '' for every field if not provided.
	 */
	clearedValue?: (fieldId: string) => FieldValue;
}

/**
 * Returns a copy of `values` with every key that maps to a hidden, governed
 * field reset to its cleared value (default ''; override via options.clearedValue).
 *
 * "Governed" means the fieldId is represented in surveyForm.surveyParts (flat
 * part's own fieldId, or nested questions[].fieldId for SurveySection parts).
 * Orphan keys — present in `values` but absent from surveyParts — pass through
 * unchanged to mirror buildCustomPropertiesSchema's unconditional-required
 * fallback for stale-surveyForm fields (clearing them would cause silent
 * validation failures on submit).
 *
 * Cascades to fixpoint: after clearing a set of hidden fields the visible set
 * is recomputed and clearing is repeated until no further fields need clearing.
 * The loop is capped at `Object.keys(values).length` iterations.
 * governedFieldIds is loop-invariant and computed once before the loop.
 *
 * V1 passthrough: when surveyForm has no surveyParts, returns `values` unchanged.
 */
export function clearHiddenFieldValues<T extends Record<string, FieldValue>>(
	surveyForm: SurveyForm | SectionedSurveyForm | undefined,
	values: T,
	options?: ClearHiddenFieldValuesOptions
): T {
	// V1 passthrough: no surveyParts means visibility is undefined → show all.
	if (!surveyForm?.surveyParts?.length) {
		return values;
	}

	const clearedValue = options?.clearedValue ?? (() => '');

	// Build the governed set once (loop-invariant). Uses the same collection
	// logic as buildCustomPropertiesSchema's surveyPartFieldIds in validation.ts:
	// SurveySection parts (_type === 'SurveySection') contribute their nested
	// questions[].fieldId; all other parts contribute their own top-level fieldId.
	// Orphan keys (in `values` but not in governedFieldIds) are never cleared.
	const governedFieldIds = new Set<string>(
		(surveyForm.surveyParts as SurveyPart[]).flatMap((part: SurveyPart) => {
			if ((part as { _type?: string })._type === 'SurveySection') {
				const questions = (part as { questions?: Array<{ fieldId?: string }> }).questions ?? [];
				return questions.map(q => q.fieldId).filter((id): id is string => !!id);
			}
			return [(part as { fieldId?: string }).fieldId].filter((id): id is string => !!id);
		})
	);

	// Maximum iterations = number of keys in values (each iteration must clear
	// at least one more field to make progress; bounded set prevents infinite loop).
	const maxIterations = Object.keys(values).length + 1;

	let current: T = values;
	let didChange = false;

	for (let i = 0; i < maxIterations; i++) {
		const visibleFieldIds = computeVisibleFields(surveyForm as SurveyForm, current);

		// Collect keys present in current that ARE governed AND NOT in the visible set.
		// Orphan keys (not in governedFieldIds) pass through unchanged.
		const hiddenKeys = Object.keys(current).filter(key => governedFieldIds.has(key) && !visibleFieldIds.has(key));

		if (hiddenKeys.length === 0) {
			// Fixpoint reached — nothing left to clear.
			break;
		}

		// Build a new object only if something actually needs clearing.
		// We check whether any hidden key has a non-cleared value to avoid
		// creating a new object when every hidden key is already ''.
		let anyActualChange = false;
		for (const key of hiddenKeys) {
			const cleared = clearedValue(key);
			if (!valuesAreEqual(current[key], cleared)) {
				anyActualChange = true;
				break;
			}
		}

		if (!anyActualChange) {
			// All hidden keys already carry the cleared value — fixpoint.
			break;
		}

		// Build updated object with hidden fields cleared.
		const next = { ...current } as T;
		for (const key of hiddenKeys) {
			(next as Record<string, FieldValue>)[key] = clearedValue(key);
		}

		current = next;
		didChange = true;
		// Loop: recompute with cleared values to catch cascading hides.
	}

	return didChange ? current : values;
}

/** Shallow equality for FieldValue (string or array reference). */
function valuesAreEqual(a: FieldValue, b: FieldValue): boolean {
	if (Array.isArray(a) && Array.isArray(b)) {
		// Two empty arrays are considered equal regardless of reference so that
		// a FileUpload field already cleared to [] does not trigger a spurious
		// extra iteration (clearedValue() creates a new [] each call).
		return a === b || (a.length === 0 && b.length === 0);
	}
	return a === b;
}
